/**
 * Render — API data becomes DOM ONLY through cloned <template> elements, textContent and a fixed
 * list of attributes. Nothing here parses HTML, so API data can never become markup or script.
 * All user-visible copy (Polish) lives in the template, never here.
 *
 * Template markup:
 *   <td data-slot="name"></td>                              textContent = data.name
 *   <a data-slot="name" data-slot-attr="href:url, title">   href = data.url (checked), title = data.title
 * Templates are Stimulus targets named `<thing>Template` with ONE root element.
 */

/** Attributes data may be written to. Anything else in data-slot-attr throws. */
const TEXT_ATTRIBUTES = new Set([
  'title',
  'alt',
  'datetime',
  'value',
  'aria-label',
  'aria-current',
  'aria-selected', // used by the combobox option template of the canon examples
  'data-id',
  'data-key',
  'data-state',
  'data-value',
  'data-variant', // row kind: group / item / day (data-kind is showState's error-kind selector)
  'data-depth',
  'data-tone', // pill / trend tone (data-state belongs to showState)
])
const BOOLEAN_ATTRIBUTES = new Set(['hidden', 'disabled'])
const URL_ATTRIBUTES = {
  href: { tags: ['A', 'AREA'], protocols: ['http:', 'https:', 'mailto:', 'tel:'] },
  src: { tags: ['IMG'], protocols: ['http:', 'https:'] },
}
const STATES = ['loading', 'empty', 'error', 'ready']

/**
 * @param {unknown} value
 * @param {string[]} [protocols]
 * @returns {string | null} absolute URL, or null when it is not a plain web/mail/phone link
 */
export function safeUrl(value, protocols = URL_ATTRIBUTES.href.protocols) {
  if (typeof value !== 'string' || value.trim() === '') return null
  try {
    const url = new URL(value, document.baseURI)
    return protocols.includes(url.protocol) ? url.href : null
  } catch {
    return null
  }
}

/** @param {Element} element @param {unknown} value */
export function setText(element, value) {
  const text = value === null || value === undefined || typeof value === 'object' ? '' : String(value)
  if (element.textContent !== text) element.textContent = text
}

// Remember template copy before fill() or showState() can replace it; removed slots are not retained.
const errorDefaults = new WeakMap()
const setErrorText = (element, value) => {
  if (!errorDefaults.has(element)) errorDefaults.set(element, element.textContent)
  const empty = value === null || value === undefined || (typeof value === 'string' && value.trim() === '')
  setText(element, empty ? errorDefaults.get(element) : value)
}

/** Boolean attributes: false, "false", "", null, undefined, 0 and "0" are false (view-models often carry String(bool)). */
const truthy = (value) => !(value === false || value === null || value === undefined || value === 0 || value === '' || value === 'false' || value === '0')

/** @param {Element} element @param {string} name @param {unknown} value */
export function setAttribute(element, name, value) {
  if (BOOLEAN_ATTRIBUTES.has(name)) return void element.toggleAttribute(name, truthy(value))
  const rule = Object.hasOwn(URL_ATTRIBUTES, name) ? URL_ATTRIBUTES[name] : null
  if (rule) {
    if (!rule.tags.includes(element.tagName)) throw new TypeError(`render: ${name} is not allowed on <${element.localName}>`)
    value = safeUrl(value, rule.protocols)
  } else if (!TEXT_ATTRIBUTES.has(name)) {
    throw new TypeError(`render: attribute "${name}" is not on the allowlist`)
  }
  if (value === null || value === undefined || typeof value === 'object') element.removeAttribute(name)
  else if (element.getAttribute(name) !== String(value)) element.setAttribute(name, String(value))
}

/** @param {HTMLTemplateElement} template @returns {Element} a fresh copy of its single root element */
export function instantiate(template) {
  const root = template?.content?.firstElementChild
  if (!root) throw new TypeError('render: <template> with one root element expected')
  return /** @type {Element} */ (document.importNode(root, true))
}

const withSelf = (root, selector) => [...(root.matches(selector) ? [root] : []), ...root.querySelectorAll(selector)]
const read = (data, key) => (Object.hasOwn(data, key) ? data[key] : undefined)

/**
 * Write a FLAT view-model into the slots of `root`. Format (dates, money, masking) in `present()`
 * before calling: fill() only places strings.
 * @param {Element} root
 * @param {Record<string, unknown>} data
 */
export function fill(root, data) {
  for (const element of withSelf(root, '[data-slot]')) {
    const write = element.dataset.slot === 'error-message' ? setErrorText : setText
    write(element, read(data, element.dataset.slot))
  }
  for (const element of withSelf(root, '[data-slot-attr]')) {
    for (const pair of element.dataset.slotAttr.split(',')) {
      const [name, key = name] = pair.split(':').map((part) => part.trim())
      setAttribute(element, name, read(data, key))
    }
  }
  return root
}

/**
 * Keyed list rendering. Existing nodes are reused and only moved when out of place, so focus,
 * scroll position and open rows survive a refresh; nothing is written when nothing changed.
 * @template T
 * @param {Element} container
 * @param {T[]} items
 * @param {{
 *   template: HTMLTemplateElement,
 *   key: string | ((item: T) => unknown),
 *   present?: (item: T, index: number) => Record<string, unknown>,
 * }} options
 */
export function renderList(container, items, { template, key, present = (item) => /** @type {any} */ (item) }) {
  /** @type {Map<string, Element>} */
  const existing = new Map()
  for (const child of [...container.children]) {
    const id = child.getAttribute('data-key')
    if (id === null || existing.has(id)) child.remove()
    else existing.set(id, child)
  }
  const seen = new Set()
  items.forEach((item, index) => {
    let id = String((typeof key === 'function' ? key(item) : item?.[key]) ?? '')
    if (id === '' || seen.has(id)) id = `${id}#${index}`
    seen.add(id)
    const node = existing.get(id) ?? instantiate(template)
    existing.delete(id)
    fill(node, present(item, index))
    if (node.getAttribute('data-key') !== id) node.setAttribute('data-key', id)
    const current = container.children[index]
    if (current !== node) container.insertBefore(node, current ?? null)
  })
  for (const node of existing.values()) node.remove()
}

/** Does a `data-kind` list ("network timeout", "*") accept this error kind? */
const kindMatches = (element, kind) => {
  const kinds = element.getAttribute('data-kind').split(/\s+/)
  return kinds.includes('*') || kinds.includes(kind)
}

/**
 * The four standard states. Sets data-state on root and shows only the children whose
 * `data-when` lists the state. Parts that belong to a nested controller are left alone.
 * For `error`: the `[data-slot="error-message"]` child gets `error.message` as text, root gets
 * `data-error-kind`, and children marked `data-kind="forbidden network"` (or "*") show only for
 * a matching `error.kind`: per-kind Polish copy stays in the template. An empty message restores
 * the slot's original template text, including when fill() has already visited it.
 * @param {Element} root
 * @param {'loading' | 'empty' | 'error' | 'ready'} state
 * @param {{ message?: string, kind?: string }} [error]
 */
export function showState(root, state, error = {}) {
  if (!STATES.includes(state)) throw new TypeError(`render: unknown state "${state}"`)
  const { message = '', kind = '' } = error ?? {}
  root.setAttribute('data-state', state)
  if (state === 'error' && kind) root.setAttribute('data-error-kind', kind)
  else root.removeAttribute('data-error-kind')
  const owner = root.closest('[data-controller]')
  const mine = (part) => part.parentElement.closest('[data-controller]') === owner
  for (const part of root.querySelectorAll('[data-when]')) {
    if (!mine(part)) continue
    let visible = part.getAttribute('data-when').split(/\s+/).includes(state)
    if (visible && state === 'error' && part.hasAttribute('data-kind')) visible = kindMatches(part, kind)
    part.toggleAttribute('hidden', !visible)
    if (visible && state === 'error') {
      for (const slot of withSelf(part, '[data-slot="error-message"]')) setErrorText(slot, message)
      for (const variant of part.querySelectorAll('[data-kind]')) {
        if (mine(variant)) variant.toggleAttribute('hidden', !kindMatches(variant, kind))
      }
    }
  }
}
